import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, SecurityContext } from '@angular/core';
import { ChatMessage } from '../../models/chat-message';
import { RecordKPRService } from '../../services/record-kpr.service';
import { RecordKPRConstants } from '../../record-kpr-constants';
import { ChatRole } from '../../enums/chat-role.enum';
import { LabelConstants } from '../../../shared/constants/label-constants';
import { ConversationHistoryDto } from '../../models/conversation-history-dto';
import { ChatRequestDto } from '../../models/chat-request-dto';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
    selector: 'app-record-kpr-chat',
    templateUrl: './record-kpr-chat.component.html',
    styleUrls: ['./record-kpr-chat.component.scss']
})
export class RecordKPRChatComponent implements OnInit, AfterViewChecked {
    @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

    messages: ChatMessage[] = [];
    userInput: string = '';
    isLoading: boolean = false;
    hasAccessToRecordKPR: boolean = false;
    recordKPRAccessErrorMessage!: string;
    readonly pageTitle = RecordKPRConstants.PageTitle;
    readonly placeholderText = RecordKPRConstants.Placeholder_Text;
    readonly typingIndicatorText = RecordKPRConstants.Typing_Indicator_Text;
    readonly warningMessage = RecordKPRConstants.Warning_Message;
    readonly newChatButtonLabel = RecordKPRConstants.New_Chat_Button;
    readonly ChatRole = ChatRole;

    private shouldScrollToBottom = false;
    private conversationHistory: ConversationHistoryDto[] = [];

    constructor(
        private recordKPRService: RecordKPRService,
        private sanitizer: DomSanitizer
    ) { }

    ngOnInit(): void {
        this.checkUserAccessToRecordKPR();
    }

    private checkUserAccessToRecordKPR(): void {
        this.recordKPRService.recordKPRViewAuthorization().subscribe({
            error: (err: any) => {
                if (err.status === LabelConstants.Forbidden_StatusCode) {
                    this.recordKPRAccessErrorMessage = err.error.detail;
                }
                this.hasAccessToRecordKPR = false;
            },
            next: (serviceData: boolean) => {
                if (serviceData) {
                    this.hasAccessToRecordKPR = serviceData;
                    this.startNewChat();
                } else {
                    this.recordKPRAccessErrorMessage = RecordKPRConstants.RecordKPR_Page_Validation;
                    this.hasAccessToRecordKPR = false;
                }
            },
        });
    }

    startNewChat(): void {
        if (!this.hasAccessToRecordKPR || this.isLoading) {
            return;
        }
        this.messages = [];
        this.userInput = '';
        this.conversationHistory = [];
        this.addInitialMessage();
    }

    ngAfterViewChecked(): void {
        if (this.shouldScrollToBottom) {
            this.scrollToBottom();
            this.shouldScrollToBottom = false;
        }
    }

    private scrollToBottom(): void {
        if (this.messagesContainer) {
            this.messagesContainer.nativeElement.scrollTop =
                this.messagesContainer.nativeElement.scrollHeight;
        }
    }

    sendMessage(): void {
        if (!this.hasAccessToRecordKPR || !this.userInput.trim() || this.isLoading) {
            return;
        }
        const trimmed = this.userInput.trim();
        // add user's message to UI and conversation history
        this.messages.push(this.createMessage(ChatRole.User, trimmed));
      this.conversationHistory.push({ role: ChatRole.User, content: trimmed });

        const request: ChatRequestDto = {
            user_input: trimmed,
            conversation_history: this.conversationHistory,
            scopes: [],
            mode: 'both',
            retrieval_mode: 'Standard',
            elims_link: null,
            security_permissions: []
        };

        this.userInput = '';
        this.isLoading = true;
        this.shouldScrollToBottom = true;

        this.recordKPRService.sendMessage(request).subscribe({
            next: (response: string) => {
                this.messages.push(this.createMessage(ChatRole.Assistant, response));
            this.conversationHistory.push({ role: ChatRole.Assistant, content: response });
                this.isLoading = false;
                this.shouldScrollToBottom = true;
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    onEnterKey(event: Event): void {
        const keyboardEvent = event as KeyboardEvent;
        if (!keyboardEvent.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    }

    private createMessage(role: ChatRole, content: string): ChatMessage {
        return {
            id: '', // TO DO: Generation of ID need to check after integration.
            role,
            content,
            timestamp: new Date()
        };
    }

    private addInitialMessage(): void {
        const initial = RecordKPRConstants.Initial_Message;
        this.messages.push(this.createMessage(ChatRole.Assistant, initial));
        this.conversationHistory.push({ role: ChatRole.Assistant, content: initial });
    }

    /**
     * Get sanitized message HTML for display
     * Supports basic Markdown: code fences (```), inline code (`), bold (**), italic (*), links ([text](url))
     * HTML is properly escaped and sanitized - no security bypass needed
     */
    getMessageHtml(message: ChatMessage): string {
        const content = message.content || '';
        
        if (message.role === ChatRole.User) {
            const escaped = this.escapeHtml(content).replace(/\n/g, '<br>');
            return this.sanitizer.sanitize(SecurityContext.HTML, escaped) || '';
        }

        const html = this.markdownToHtml(content);
        return this.sanitizer.sanitize(SecurityContext.HTML, html) || '';
    }

    /**
     * Convert Markdown to safe HTML
     * Supports:
     * - Headers (#, ##, ###, ####)
     * - code fences (```...```) → <pre><code>
     * - inline code `...` → <code>
     * - bold **text** → <strong>
     * - italic *text* → <em>
     * - links [text](url) → <a>
     * - numbered lists (1., 2., 3., etc.)
     * - unordered lists (-, *, +)
     */
    private markdownToHtml(md: string): string {
        if (!md) return '';

        let out = this.escapeHtml(md);

        // Preserve code blocks before processing other markdown
        const codeBlocks: string[] = [];
        out = out.replace(/```([\s\S]*?)```/g, (match, code) => {
            const placeholder = `__CODE_BLOCK_${codeBlocks.length}__`;
            codeBlocks.push(`<pre><code>${code}</code></pre>`);
            return placeholder;
        });

        // Inline code `...` (before bold/italic to avoid conflicts)
        out = out.replace(/`([^`\n]+)`/g, '<code>$1</code>');

        // Links [text](url) (before bold/italic)
        out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (match, text, url) => {
            // Basic security check for javascript: URLs
            if (url.toLowerCase().startsWith('javascript:')) {
                return text + ' [blocked]';
            }
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
        });

        // Bold **text** (must be before italic to avoid conflicts)
        out = out.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');

        // Italic *text* (check that it's not part of **)
        out = out.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>');

        // Headers: # Header → <h1>, ## Header → <h2>, etc.
        out = out.replace(/^##### (.+)$/gm, '<h5>$1</h5>');
        out = out.replace(/^#### (.+)$/gm, '<h4>$1</h4>');
        out = out.replace(/^### (.+)$/gm, '<h3>$1</h3>');
        out = out.replace(/^## (.+)$/gm, '<h2>$1</h2>');
        out = out.replace(/^# (.+)$/gm, '<h1>$1</h1>');

        // Process lists: numbered and unordered
        out = this.processLists(out);

        // Preserve lists before processing line breaks
        const listPlaceholders: string[] = [];
        out = out.replace(/<(ol|ul)>[\s\S]*?<\/\1>/g, (match) => {
            const placeholder = `__LIST_${listPlaceholders.length}__`;
            listPlaceholders.push(match);
            return placeholder;
        });

        // Paragraphs: double newlines become </p><p>
        out = out.replace(/\r?\n\r?\n+/g, '</p><p>');
        out = '<p>' + out + '</p>';

        // Line breaks: single newlines become <br>
        out = out.replace(/\r?\n/g, '<br>');

        // Restore lists
        listPlaceholders.forEach((list, index) => {
            out = out.replace(`__LIST_${index}__`, list);
        });

        // Restore code blocks
        codeBlocks.forEach((block, index) => {
            out = out.replace(`__CODE_BLOCK_${index}__`, block);
        });

        return out;
    }

    /**
     * Process markdown lists (numbered and unordered)
     */
    private processLists(text: string): string {
        let result = text;

        // Numbered lists: 1. item, 2. item, etc.
        // Group consecutive numbered items and wrap in <ol>
        result = result.replace(/(?:^|\n)(\d+)\. (.+?)(?=\n(?:\d+\.|\D)|$)/gm, (fullMatch) => {
            // Extract all list items in this group
            const items = fullMatch.split(/\n/).filter(line => /^\d+\./.test(line));
            if (items.length === 0) return fullMatch;

            const listHtml = items
                .map(item => {
                    const content = item.replace(/^\d+\.\s*/, '').trim();
                    return `<li>${content}</li>`;
                })
                .join('');

            return `<ol>${listHtml}</ol>`;
        });

        // Unordered lists: -, *, + followed by space
        result = result.replace(/(?:^|\n)([-*+]) (.+?)(?=\n(?:[-*+]|\D)|$)/gm, (fullMatch) => {
            // Extract all list items in this group
            const items = fullMatch.split(/\n/).filter(line => /^[-*+]\s/.test(line));
            if (items.length === 0) return fullMatch;

            const listHtml = items
                .map(item => {
                    const content = item.replace(/^[-*+]\s+/, '').trim();
                    return `<li>${content}</li>`;
                })
                .join('');

            return `<ul>${listHtml}</ul>`;
        });

        return result;
    }

    /**
     * Escape HTML special characters to prevent XSS
     */
    private escapeHtml(str: string): string {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
}
